import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  EVENT_TYPES_CANON, DAYS_CANON, TIME_RANGES_CANON, TECH_CANON,
  BUDGET_MODELS_CANON, DISTRICTS_CANON,
  EVENT_TYPE_LABELS, DAY_LABELS, TIME_RANGE_LABELS, TECH_LABELS,
  BUDGET_MODEL_LABELS, STATUS_LABELS, STATUS_COLORS, FIELD_LABELS,
} from '../constants/canonicalEnums';

const API = process.env.REACT_APP_BACKEND_URL;

// --- helpers ---
const labelize = (val, table) => (val ? (table[val] || val) : '—');
const labelList = (arr, table) => (arr && arr.length ? arr.map(v => table[v] || v).join(', ') : '—');

const emptyConfirm = {
  title: '',
  event_type: '',
  expected_attendees: '',
  preferred_districts: [],
  preferred_days: [],
  preferred_time_ranges: [],
  venue_budget_model: '',
  technical_needs: [],
  alcohol_needed: false,
  outside_materials_needed: false,
  table_setup_needed: false,
  notes: '',
};

// --- small chip components ---
const Chip = ({ children, tone = 'zinc', testId }) => {
  const tones = {
    zinc: 'bg-zinc-100 text-zinc-700',
    orange: 'bg-[#1E3A8A]/10 text-[#1E3A8A] border border-[#1E3A8A]/30',
    amber: 'bg-amber-100 text-amber-800 border border-amber-300',
    emerald: 'bg-emerald-100 text-emerald-700 border border-emerald-300',
  };
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${tones[tone] || tones.zinc}`}>
      {children}
    </span>
  );
};

const StatusBadge = ({ status }) => {
  const label = STATUS_LABELS[status] || status || '—';
  const color = STATUS_COLORS[status] || 'bg-zinc-200 text-zinc-700';
  return (
    <span data-testid={`er-status-${status}`} className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${color}`}>
      {label}
    </span>
  );
};

const ReadinessBar = ({ value }) => {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v === 100 ? 'bg-emerald-500' : v >= 50 ? 'bg-[#1E3A8A]' : 'bg-amber-400';
  return (
    <div data-testid="er-readiness" className="space-y-1">
      <div className="flex justify-between text-xs text-zinc-600">
        <span>Hazırlık</span>
        <span className="font-bold tabular-nums">{v}%</span>
      </div>
      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${v}%` }} />
      </div>
    </div>
  );
};

// --- AI Talep Analizi Modal ---
const AIAnalysisModal = ({ open, onClose, request, onConfirmed }) => {
  const [freeText, setFreeText] = useState('');
  const [extracted, setExtracted] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [missing, setMissing] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [form, setForm] = useState(emptyConfirm);

  useEffect(() => {
    if (!open || !request) return;
    setFreeText(request.free_text || '');
    if (request.extracted_filters) {
      const ex = request.extracted_filters;
      setExtracted(ex);
      setFollowUps(ex.follow_up_questions || request.follow_up_questions || []);
      setMissing(ex.missing_fields || request.missing_fields || []);
    } else {
      setExtracted(null);
      setFollowUps([]);
      setMissing(request.missing_fields || []);
    }
    setForm({
      title: request.title || '',
      event_type: request.event_type || '',
      expected_attendees: request.expected_attendees != null ? String(request.expected_attendees) : '',
      preferred_districts: request.preferred_districts || [],
      preferred_days: request.preferred_days || [],
      preferred_time_ranges: request.preferred_time_ranges || [],
      venue_budget_model: request.venue_budget_model || '',
      technical_needs: request.technical_needs || [],
      alcohol_needed: !!request.alcohol_needed,
      outside_materials_needed: !!request.outside_materials_needed,
      table_setup_needed: !!request.table_setup_needed,
      notes: request.notes || '',
    });
  }, [open, request]);

  const readinessLocal = useMemo(() => {
    const required = ['title', 'event_type', 'expected_attendees', 'preferred_districts',
                      'preferred_days', 'preferred_time_ranges', 'venue_budget_model'];
    const filled = required.filter(k => {
      const v = form[k];
      if (Array.isArray(v)) return v.length > 0;
      if (typeof v === 'string') return v.trim().length > 0;
      return v != null && v !== 0;
    }).length;
    return Math.round((filled / required.length) * 100);
  }, [form]);

  if (!open) return null;

  const runAnalyze = async () => {
    if (!freeText.trim()) {
      toast.error('Önce talebini birkaç cümleyle anlat.');
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await axios.post(`${API}/api/event-requests/${request.id}/analyze`, { free_text: freeText });
      const ex = data.extracted_filters || {};
      setExtracted(ex);
      setFollowUps(data.follow_up_questions || []);
      setMissing(data.missing_fields || []);
      // Form'a AI önerisini uygula (boş olanları doldur)
      setForm(prev => ({
        ...prev,
        title: ex.title || prev.title,
        event_type: ex.event_type || prev.event_type,
        expected_attendees: ex.expected_attendees != null ? String(ex.expected_attendees) : prev.expected_attendees,
        preferred_districts: ex.preferred_districts?.length ? ex.preferred_districts : prev.preferred_districts,
        preferred_days: ex.preferred_days?.length ? ex.preferred_days : prev.preferred_days,
        preferred_time_ranges: ex.preferred_time_ranges?.length ? ex.preferred_time_ranges : prev.preferred_time_ranges,
        venue_budget_model: ex.venue_budget_model || prev.venue_budget_model,
        technical_needs: ex.technical_needs?.length ? ex.technical_needs : prev.technical_needs,
        alcohol_needed: ex.alcohol_needed ?? prev.alcohol_needed,
        outside_materials_needed: ex.outside_materials_needed ?? prev.outside_materials_needed,
        table_setup_needed: ex.table_setup_needed ?? prev.table_setup_needed,
        notes: ex.notes || prev.notes,
      }));
      toast.success('AI analizi hazır 🎯 Aşağıdan düzenleyip onayla.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analiz başarısız.');
    } finally {
      setAnalyzing(false);
    }
  };

  const toggleListVal = (key, value) => {
    setForm(prev => {
      const arr = prev[key] || [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter(x => x !== value) : [...arr, value] };
    });
  };

  const confirmAnalysis = async () => {
    setConfirming(true);
    try {
      const payload = {
        title: form.title.trim() || null,
        event_type: form.event_type || null,
        expected_attendees: form.expected_attendees ? parseInt(form.expected_attendees) : null,
        preferred_districts: form.preferred_districts,
        preferred_days: form.preferred_days,
        preferred_time_ranges: form.preferred_time_ranges,
        venue_budget_model: form.venue_budget_model || null,
        technical_needs: form.technical_needs,
        alcohol_needed: form.alcohol_needed,
        outside_materials_needed: form.outside_materials_needed,
        table_setup_needed: form.table_setup_needed,
        notes: form.notes || null,
      };
      const { data } = await axios.post(`${API}/api/event-requests/${request.id}/confirm-analysis`, payload);
      toast.success('Onaylandı! Eşleşmeye hazırsın 🚀');
      onConfirmed(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Onaylanamadı.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div data-testid="ai-analysis-modal" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white max-w-2xl w-full rounded-2xl shadow-xl my-8">
        <div className="flex items-start justify-between p-6 border-b border-zinc-100">
          <div>
            <p className="text-xs font-bold text-[#1E3A8A] uppercase tracking-wide">AI Talep Analizi</p>
            <h2 className="text-2xl font-bold mt-1">Talebini birkaç cümleyle anlat</h2>
            <p className="text-sm text-zinc-600 mt-1">AI metnini filtrelere çevirir — mekân seçmez, skor vermez. Son söz sende.</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-lg text-zinc-500" data-testid="ai-modal-close" aria-label="Kapat">✕</button>
        </div>

        <div className="p-6 space-y-6">
          {/* 1. Free text */}
          <div>
            <label className="block mb-2 font-semibold text-sm">Etkinlik hakkında serbest açıklama</label>
            <textarea
              data-testid="ai-modal-freetext"
              value={freeText}
              onChange={e => setFreeText(e.target.value)}
              placeholder="Örn: Kadıköy civarında cumartesi günü 30 kişilik bir resim atölyesi yapacağım, projeksiyon lazım, bütçem mütevazı..."
              className="w-full px-4 py-3 border-2 border-zinc-200 rounded-xl min-h-28 focus:outline-none focus:border-[#1E3A8A] transition-colors"
            />
            <button
              onClick={runAnalyze}
              disabled={analyzing}
              data-testid="ai-modal-analyze"
              className="mt-3 px-6 py-2.5 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors disabled:opacity-50"
            >
              {analyzing ? 'Analiz ediliyor…' : 'Analiz Et 🧠'}
            </button>
          </div>

          {/* 2. AI follow-up questions */}
          {followUps.length > 0 && (
            <div data-testid="ai-modal-followups" className="bg-sky-50 border border-sky-200 rounded-xl p-4">
              <p className="text-sm font-bold text-sky-900 mb-2">AI&apos;nın eksik bilgi için soruları:</p>
              <ul className="space-y-1.5">
                {followUps.map((q, i) => (
                  <li key={i} className="text-sm text-sky-800 flex gap-2"><span>•</span><span>{q}</span></li>
                ))}
              </ul>
            </div>
          )}

          {/* 3. Editable summary */}
          {(extracted || request.status !== 'taslak') && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <h3 className="font-bold text-zinc-800">Önerilen filtreler — düzenleyip onayla</h3>
                <div className="text-xs"><strong>{readinessLocal}%</strong> hazır</div>
              </div>

              {/* Title */}
              <div>
                <label className="text-sm font-semibold block mb-1">Talep adı</label>
                <input
                  data-testid="ai-modal-field-title"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Cumartesi Atölyesi"
                  className="w-full px-3 py-2 border-2 border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A]"
                />
              </div>

              {/* Event type */}
              <div>
                <label className="text-sm font-semibold block mb-2">Etkinlik türü</label>
                <div className="flex flex-wrap gap-2">
                  {EVENT_TYPES_CANON.map(o => (
                    <button key={o.value} type="button"
                      data-testid={`ai-modal-event-type-${o.value}`}
                      onClick={() => setForm({ ...form, event_type: o.value })}
                      className={`px-3 py-1.5 text-sm rounded-full border-2 transition-colors ${
                        form.event_type === o.value
                          ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
                          : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'
                      }`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Attendees */}
              <div>
                <label className="text-sm font-semibold block mb-1">Beklenen katılımcı sayısı</label>
                <input
                  type="number" min="1"
                  data-testid="ai-modal-field-attendees"
                  value={form.expected_attendees}
                  onChange={e => setForm({ ...form, expected_attendees: e.target.value })}
                  className="w-32 px-3 py-2 border-2 border-zinc-200 rounded-lg focus:outline-none focus:border-[#1E3A8A]"
                />
              </div>

              {/* Districts */}
              <div>
                <label className="text-sm font-semibold block mb-2">Tercih edilen ilçeler</label>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto border-2 border-zinc-100 rounded-lg p-2">
                  {DISTRICTS_CANON.map(d => (
                    <button key={d} type="button"
                      data-testid={`ai-modal-district-${d}`}
                      onClick={() => toggleListVal('preferred_districts', d)}
                      className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                        form.preferred_districts.includes(d)
                          ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
                          : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'
                      }`}>
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Days */}
              <div>
                <label className="text-sm font-semibold block mb-2">Tercih edilen günler</label>
                <div className="flex flex-wrap gap-2">
                  {DAYS_CANON.map(d => (
                    <button key={d.value} type="button"
                      data-testid={`ai-modal-day-${d.value}`}
                      onClick={() => toggleListVal('preferred_days', d.value)}
                      className={`px-3 py-1.5 text-sm rounded-full border-2 transition-colors ${
                        form.preferred_days.includes(d.value)
                          ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
                          : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'
                      }`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time ranges */}
              <div>
                <label className="text-sm font-semibold block mb-2">Saat aralıkları</label>
                <div className="flex flex-wrap gap-2">
                  {TIME_RANGES_CANON.map(t => (
                    <button key={t.value} type="button"
                      data-testid={`ai-modal-time-${t.value}`}
                      onClick={() => toggleListVal('preferred_time_ranges', t.value)}
                      className={`px-3 py-1.5 text-sm rounded-full border-2 transition-colors ${
                        form.preferred_time_ranges.includes(t.value)
                          ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
                          : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Budget */}
              <div>
                <label className="text-sm font-semibold block mb-2">Bütçe modeli</label>
                <div className="flex flex-col gap-1.5">
                  {BUDGET_MODELS_CANON.map(b => (
                    <button key={b.value} type="button"
                      data-testid={`ai-modal-budget-${b.value}`}
                      onClick={() => setForm({ ...form, venue_budget_model: b.value })}
                      className={`text-left px-3 py-2 text-sm rounded-lg border-2 transition-colors ${
                        form.venue_budget_model === b.value
                          ? 'bg-[#1E3A8A]/10 border-[#1E3A8A] text-[#1E3A8A]'
                          : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'
                      }`}>
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tech needs */}
              <div>
                <label className="text-sm font-semibold block mb-2">Teknik ihtiyaçlar (opsiyonel)</label>
                <div className="flex flex-wrap gap-2">
                  {TECH_CANON.map(t => (
                    <button key={t.value} type="button"
                      data-testid={`ai-modal-tech-${t.value}`}
                      onClick={() => toggleListVal('technical_needs', t.value)}
                      className={`px-3 py-1.5 text-sm rounded-full border-2 transition-colors ${
                        form.technical_needs.includes(t.value)
                          ? 'bg-[#1E3A8A] text-white border-[#1E3A8A]'
                          : 'bg-white text-zinc-700 border-zinc-200 hover:border-[#1E3A8A]'
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Confirm */}
              <div className="flex gap-3 pt-2 sticky bottom-0 bg-white">
                <button
                  onClick={confirmAnalysis}
                  disabled={confirming || readinessLocal < 100}
                  data-testid="ai-modal-confirm"
                  className="px-6 py-3 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors disabled:opacity-50"
                >
                  {confirming ? 'Onaylanıyor…' : '✓ Onayla & Eşleşmeye Hazır'}
                </button>
                <button onClick={onClose} className="px-5 py-3 bg-white border-2 border-zinc-300 hover:border-zinc-500 text-zinc-700 rounded-xl font-bold transition-colors">
                  Şimdi Değil
                </button>
              </div>
              {readinessLocal < 100 && (
                <p className="text-xs text-amber-700 -mt-2">Onaylamak için tüm zorunlu alanları doldur (şu an %{readinessLocal}).</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- Card ---
const EventRequestCard = ({ item, onAnalyze, onEdit, onActivate, onDelete }) => {
  const status = item.status || 'taslak';
  const isReady = status === 'eslesmeye_hazir';
  const isMissing = ['taslak', 'bilgi_eksik'].includes(status);
  const isAnalyzeReady = status === 'analize_hazir';
  const inAdmin = status === 'admin_incelemesinde';
  const matched = status === 'eslesti';

  return (
    <div className={`bg-white rounded-2xl p-6 transition-colors ${item.active ? 'border-2 border-[#1E3A8A]' : 'border-2 border-zinc-200'}`} data-testid={`er-card-${item.id}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="text-xl font-bold">{item.title || 'Etkinlik'}</h3>
            <StatusBadge status={status} />
            {item.active && status !== 'eslesti' && (
              <span className="px-2 py-0.5 rounded-full bg-[#1E3A8A]/10 text-[#1E3A8A] text-xs font-bold">AKTİF</span>
            )}
          </div>
          {item.free_text && (
            <p className="text-sm text-zinc-500 italic line-clamp-2">&ldquo;{item.free_text}&rdquo;</p>
          )}
        </div>
      </div>

      {/* Readiness bar */}
      <div className="mb-4">
        <ReadinessBar value={item.readiness_score || 0} />
      </div>

      {/* Summary grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm mb-4">
        <div><span className="text-zinc-500 block text-xs">Etkinlik türü</span><span className="font-medium">{labelize(item.event_type, EVENT_TYPE_LABELS)}</span></div>
        <div><span className="text-zinc-500 block text-xs">Katılımcı</span><span className="font-medium">{item.expected_attendees || '—'} kişi</span></div>
        <div><span className="text-zinc-500 block text-xs">Bütçe</span><span className="font-medium">{labelize(item.venue_budget_model, BUDGET_MODEL_LABELS)}</span></div>
        <div className="col-span-2 sm:col-span-3"><span className="text-zinc-500 block text-xs">İlçeler</span><span className="font-medium">{(item.preferred_districts || []).join(', ') || '—'}</span></div>
        <div><span className="text-zinc-500 block text-xs">Günler</span><span className="font-medium">{labelList(item.preferred_days, DAY_LABELS)}</span></div>
        <div><span className="text-zinc-500 block text-xs">Saat aralıkları</span><span className="font-medium">{labelList(item.preferred_time_ranges, TIME_RANGE_LABELS)}</span></div>
        <div><span className="text-zinc-500 block text-xs">Teknik</span><span className="font-medium">{labelList(item.technical_needs, TECH_LABELS)}</span></div>
      </div>

      {/* Mandatory needs row */}
      {(item.alcohol_needed || item.outside_materials_needed || item.table_setup_needed) && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {item.alcohol_needed && <Chip tone="orange" testId={`er-need-alcohol-${item.id}`}>Alkol gerekli</Chip>}
          {item.outside_materials_needed && <Chip tone="orange" testId={`er-need-outside-${item.id}`}>Dışarıdan malzeme</Chip>}
          {item.table_setup_needed && <Chip tone="orange" testId={`er-need-table-${item.id}`}>Masa düzeni gerekli</Chip>}
        </div>
      )}

      {/* Missing fields */}
      {item.missing_fields?.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid={`er-missing-${item.id}`}>
          <p className="text-xs font-bold text-amber-900 mb-1.5">Eksik bilgiler:</p>
          <div className="flex flex-wrap gap-1.5">
            {item.missing_fields.map(f => (
              <Chip key={f} tone="amber">{FIELD_LABELS[f] || f}</Chip>
            ))}
          </div>
        </div>
      )}

      {/* AI follow-up questions */}
      {item.follow_up_questions?.length > 0 && status !== 'eslesmeye_hazir' && status !== 'eslesti' && (
        <div className="mb-4 p-3 bg-sky-50 border border-sky-200 rounded-xl" data-testid={`er-followups-${item.id}`}>
          <p className="text-xs font-bold text-sky-900 mb-1.5">AI&apos;nın soruları:</p>
          <ul className="space-y-1">
            {item.follow_up_questions.slice(0, 3).map((q, i) => (
              <li key={i} className="text-xs text-sky-800">• {q}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap pt-2 border-t border-zinc-100">
        {isReady && item.active && (
          <Link to="/swipe" data-testid={`er-swipe-${item.id}`}
            className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-lg text-sm font-bold transition-colors">
            🔥 Eşleşmeye Başla
          </Link>
        )}
        {(isMissing || isAnalyzeReady) && (
          <button onClick={() => onAnalyze(item)} data-testid={`er-analyze-${item.id}`}
            className="px-4 py-2 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-lg text-sm font-bold transition-colors">
            🧠 AI Talep Analizi
          </button>
        )}
        {inAdmin && (
          <span className="px-4 py-2 bg-violet-50 border border-violet-200 text-violet-700 rounded-lg text-sm font-bold" data-testid={`er-admin-review-${item.id}`}>
            ⏳ Admin İnceliyor
          </span>
        )}
        {matched && (
          <Link to="/organizer/matches" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold transition-colors" data-testid={`er-matched-${item.id}`}>
            ✓ Eşleşmeyi Gör
          </Link>
        )}
        <button onClick={() => onEdit(item)} data-testid={`er-edit-${item.id}`}
          className="px-4 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-700 rounded-lg text-sm font-semibold transition-colors">
          Düzenle
        </button>
        {!item.active && status !== 'eslesti' && (
          <button onClick={() => onActivate(item.id)} data-testid={`er-activate-${item.id}`}
            className="px-4 py-2 bg-white border-2 border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-lg text-sm font-bold transition-colors">
            Aktif Et
          </button>
        )}
        <button onClick={() => onDelete(item.id)} data-testid={`er-delete-${item.id}`} aria-label="Sil"
          className="ml-auto px-3 py-2 bg-white border border-zinc-200 hover:border-red-400 hover:text-red-500 text-zinc-500 rounded-lg text-sm transition-colors">
          Sil
        </button>
      </div>
    </div>
  );
};

// --- Manual edit form (basit, sadece title + free_text + notes) ---
const QuickEditForm = ({ initial, onSave, onCancel, saving }) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [freeText, setFreeText] = useState(initial?.free_text || '');
  const [notes, setNotes] = useState(initial?.notes || '');
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ title, free_text: freeText, notes }); }}
      className="space-y-4 bg-white border-2 border-zinc-200 rounded-2xl p-6" data-testid="er-quick-form">
      <div>
        <label className="block mb-2 font-semibold text-sm">Talep adı *</label>
        <input value={title} onChange={e => setTitle(e.target.value)} required
          placeholder='Örn. "Cumartesi Atölyesi"' data-testid="er-form-title"
          className="w-full px-4 py-3 border-2 border-zinc-200 rounded-xl focus:outline-none focus:border-[#1E3A8A]" />
      </div>
      <div>
        <label className="block mb-2 font-semibold text-sm">Etkinlik hakkında serbest açıklama</label>
        <textarea value={freeText} onChange={e => setFreeText(e.target.value)} rows={4}
          placeholder="Kim için, ne zaman, nerede, ne kadar bütçe vb. — AI bu metinden filtreleri çıkaracak."
          data-testid="er-form-freetext"
          className="w-full px-4 py-3 border-2 border-zinc-200 rounded-xl focus:outline-none focus:border-[#1E3A8A]" />
      </div>
      <div>
        <label className="block mb-2 font-semibold text-sm">Notlar (opsiyonel)</label>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
          data-testid="er-form-notes"
          className="w-full px-4 py-3 border-2 border-zinc-200 rounded-xl focus:outline-none focus:border-[#1E3A8A]" />
      </div>
      <div className="flex gap-3">
        <button type="submit" disabled={saving || !title.trim()} data-testid="er-form-submit"
          className="px-6 py-3 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors disabled:opacity-50">
          {saving ? 'Kaydediliyor…' : 'Kaydet & AI Analizine Geç →'}
        </button>
        <button type="button" onClick={onCancel} data-testid="er-form-cancel"
          className="px-5 py-3 bg-white border-2 border-zinc-300 hover:border-zinc-500 text-zinc-700 rounded-xl font-bold transition-colors">
          İptal
        </button>
      </div>
    </form>
  );
};

// --- Main ---
const EventRequests = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [aiTarget, setAiTarget] = useState(null);

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/event-requests/me`);
      setItems(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveQuick = async (payload) => {
    setSaving(true);
    try {
      let saved;
      if (editing) {
        const { data } = await axios.put(`${API}/api/event-requests/${editing.id}`, {
          title: payload.title,
          free_text: payload.free_text,
          notes: payload.notes,
          // Keep existing fields
          event_type: editing.event_type,
          expected_attendees: editing.expected_attendees,
          preferred_districts: editing.preferred_districts || [],
          preferred_days: editing.preferred_days || [],
          preferred_time_ranges: editing.preferred_time_ranges || [],
          avg_ticket_price: editing.avg_ticket_price || 0,
          venue_budget_model: editing.venue_budget_model,
          technical_needs: editing.technical_needs || [],
          alcohol_needed: !!editing.alcohol_needed,
          outside_materials_needed: !!editing.outside_materials_needed,
          table_setup_needed: !!editing.table_setup_needed,
          needs_two_person_table_setup: !!editing.needs_two_person_table_setup,
          min_table_size_expectation: editing.min_table_size_expectation || null,
          needs_personal_workspace: !!editing.needs_personal_workspace,
        });
        saved = data;
        toast.success('Güncellendi');
      } else {
        const { data } = await axios.post(`${API}/api/event-requests`, {
          title: payload.title,
          free_text: payload.free_text,
          notes: payload.notes,
        });
        saved = data;
        toast.success("Talebin oluşturuldu. AI Analizi'ne geçelim 🧠");
      }
      setShowForm(false);
      setEditing(null);
      await fetchItems();
      // Yeni talep ise hemen AI Analiz modal'ını aç
      if (!editing && saved) {
        setAiTarget(saved);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleAnalyze = (item) => setAiTarget(item);

  const handleConfirmed = async () => {
    setAiTarget(null);
    await fetchItems();
  };

  const handleActivate = async (id) => {
    try {
      await axios.post(`${API}/api/event-requests/${id}/activate`, {});
      toast.success('Aktif edildi');
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu talebi silmek istediğine emin misin?')) return;
    try {
      await axios.delete(`${API}/api/event-requests/${id}`);
      toast.success('Silindi');
      await fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  if (loading) return <div className="p-8 text-center text-zinc-500">Yükleniyor…</div>;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-sm text-[#1E3A8A] font-bold mb-1">Etkinlik Taleplerim 🎯</p>
          <h1 className="text-3xl font-bold">Eşleşmeye götüren tek panel</h1>
          <p className="mt-2 text-zinc-600 text-sm">
            Her etkinliğin için ayrı talep oluştur. AI metnini filtrelere çevirir; eşleşmeyi siz onayladıktan sonra adminin onayladığı mekânları görürsün.
          </p>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="er-new"
          className="px-6 py-3 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors shadow-sm">
          + Yeni Talep
        </button>
      </div>

      {showForm && (
        <div className="mb-6">
          <QuickEditForm
            initial={editing}
            saving={saving}
            onSave={handleSaveQuick}
            onCancel={() => { setShowForm(false); setEditing(null); }}
          />
        </div>
      )}

      {items.length === 0 && !showForm ? (
        <div className="bg-zinc-50 border-2 border-zinc-200 rounded-2xl p-10 text-center">
          <h2 className="text-xl font-bold mb-2">Henüz etkinlik talebin yok</h2>
          <p className="text-sm text-zinc-600 mb-5">İlk talebini oluştur, AI birkaç saniyede filtrelere çevirsin.</p>
          <button onClick={() => setShowForm(true)} data-testid="er-first"
            className="px-6 py-3 bg-[#1E3A8A] hover:bg-[#1E3A8A] text-white rounded-xl font-bold transition-colors">
            İlk Talebimi Oluştur →
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="er-list">
          {items.map(it => (
            <EventRequestCard
              key={it.id}
              item={it}
              onAnalyze={handleAnalyze}
              onEdit={(item) => { setEditing(item); setShowForm(true); }}
              onActivate={handleActivate}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <AIAnalysisModal
        open={!!aiTarget}
        request={aiTarget || {}}
        onClose={() => setAiTarget(null)}
        onConfirmed={handleConfirmed}
      />
    </div>
  );
};

export default EventRequests;
